import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { incrementUsage } from "@/hooks/useUsageLimits";

interface UrlMetadata {
  title?: string;
  description?: string;
  image?: string;
  url: string;
}

interface PasteContentData {
  link?: string;
  textContent?: string;
  roomId?: string;
}

export const useContentPaste = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const { toast } = useToast();

  const getContentType = (url: string): string => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    }
    if (url.includes('.pdf')) {
      return 'pdf';
    }
    // Return 'web' for websites that need PDF conversion
    return 'web';
  };

  const getTitleFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + urlObj.pathname;
    } catch {
      return url;
    }
  };

  const createTextContent = async (textContent: string) => {
    const normalized = textContent.replace(/\r\n/g, "\n").trim();
    if (!normalized) throw new Error("Text content is empty");

    const firstLine = (normalized.split("\n")[0] || "Text Note").trim();
    const title = firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine;

    return {
      title: title || `Text Note - ${new Date().toLocaleDateString()}`,
      type: 'text',
      storage_path: null,
      conversion_status: 'pending', // Will be converted to PDF
    };
  };

  const fetchUrlMetadata = async (url: string): Promise<UrlMetadata | null> => {
    try {
      setIsFetchingMetadata(true);
      console.log('Fetching metadata for:', url);

      const { data, error } = await supabase.functions.invoke('fetch-url-metadata', {
        body: { url },
      });

      if (error) throw error;

      if (data?.success && data?.metadata) {
        console.log('Metadata fetched:', data.metadata);
        return data.metadata;
      }

      return null;
    } catch (error) {
      console.error('Error fetching URL metadata:', error);
      return null;
    } finally {
      setIsFetchingMetadata(false);
    }
  };

  const createUrlContent = async (link: string) => {
    // Fetch metadata for the URL
    const metadata = await fetchUrlMetadata(link);
    const contentType = getContentType(link);
    
    // Determine conversion status
    const needsConversion = contentType === 'web';

    return {
      title: metadata?.title || getTitleFromUrl(link),
      type: contentType,
      source_url: link,
      thumbnail_url: metadata?.image || null,
      conversion_status: needsConversion ? 'pending' : 'not_needed',
    };
  };

  const submitContent = async (data: PasteContentData) => {
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("You must be logged in to add content");
      }

      let contentData;

      if (data.link) {
        contentData = await createUrlContent(data.link);
      } else if (data.textContent) {
        contentData = await createTextContent(data.textContent);
      } else {
        throw new Error("No content provided");
      }

      const { data: insertedContent, error: insertError } = await supabase
        .from('content')
        .insert({
          ...contentData,
          user_id: user.id,
          visibility: 'private',
          room_id: data.roomId || null,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Track usage for content uploads
      await incrementUsage("content_uploads");

      // Trigger PDF conversion for web URLs and text content
      if (contentData.type === 'web') {
        supabase.functions
          .invoke('convert-content-to-pdf', {
            body: {
              contentId: insertedContent.id,
              type: 'web',
              sourceUrl: contentData.source_url,
            },
          })
          .catch((err) => console.error('PDF conversion failed:', err));
      }

      if (contentData.type === 'text') {
        supabase.functions
          .invoke('convert-content-to-pdf', {
            body: {
              contentId: insertedContent.id,
              type: 'text',
              textContent: data.textContent,
            },
          })
          .catch((err) => console.error('PDF conversion failed:', err));
      }

      // Trigger transcript extraction for YouTube content
      if (contentData.type === 'youtube') {
        supabase.functions
          .invoke('extract-media-transcript', {
            body: { contentId: insertedContent.id },
          })
          .catch((err) => console.error('YouTube transcript extraction failed:', err));
      }

      // Trigger refresh of content sections
      window.dispatchEvent(new CustomEvent("content-uploaded"));

      return { success: true, contentId: insertedContent.id, contentType: contentData.type };
    } catch (error) {
      console.error("Error submitting content:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add content",
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitContent,
    isSubmitting,
    isFetchingMetadata,
  };
};
